import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Package, 
  Plane, 
  Clock, 
  Box, 
  ArrowRight,
  CheckCircle,
  Warehouse,
  Send,
  Globe,
  ShieldCheck
} from "lucide-react";

const ServicesPage = () => {
  const services = [
    {
      id: "versand",
      icon: Send,
      secondaryIcon: Globe,
      title: "Verpackung & Versand",
      subtitle: "National & International",
      description: "Versenden Sie Dokumente und Pakete einfach und sicher nach Europa und weltweit. Wir arbeiten mit allen führenden Paketdiensten zusammen und finden für Sie die beste Lösung.",
      features: [
        "UPS, DHL Express, DPD, GLS, FedEx",
        "Nationaler und internationaler Versand",
        "Sendungsverfolgung in Echtzeit",
        "Versicherungsschutz für Ihre Sendungen",
        "Abholung und Zustellung",
        "Zollabwicklung für internationale Sendungen"
      ],
      image: "https://customer-assets.emergentagent.com/job_3f8853ef-18d6-476f-b97c-eda985cb2340/artifacts/hvocsgmu_Zusteller_3.jpg"
    },
    {
      id: "express",
      icon: Plane,
      secondaryIcon: Clock,
      title: "Express & Luftfracht",
      subtitle: "Schnell & Zuverlässig",
      description: "Wenn es schnell gehen muss: Mit unseren Express- und Luftfracht-Services erreichen Ihre Sendungen ihr Ziel in kürzester Zeit – weltweit.",
      features: [
        "Same Day & Next Day Lieferung",
        "Luftfracht weltweit",
        "Kurierdienste für eilige Dokumente",
        "Termintreue Zustellung",
        "24/7 Sendungsverfolgung",
        "Express-Zollabwicklung"
      ],
      image: "https://customer-assets.emergentagent.com/job_3f8853ef-18d6-476f-b97c-eda985cb2340/artifacts/ed08gqs9_Luftfracht_2.jpg"
    },
    {
      id: "fulfillment",
      icon: Warehouse,
      secondaryIcon: Package,
      title: "Fulfillment & E-Commerce",
      subtitle: "Alles aus einer Hand",
      description: "Konzentrieren Sie sich auf Ihr Kerngeschäft – wir kümmern uns um Lagerung, Kommissionierung und Versand Ihrer Online-Bestellungen.",
      features: [
        "Lagerhaltung und Bestandsmanagement",
        "Pick & Pack Service",
        "Shop-Anbindung (Shopify, WooCommerce, etc.)",
        "Retourenmanagement",
        "Individuelle Verpackung",
        "Echtzeit-Bestandsübersicht"
      ],
      image: "https://customer-assets.emergentagent.com/job_3f8853ef-18d6-476f-b97c-eda985cb2340/artifacts/egvlrw92_Ecommerce.jpg"
    },
    {
      id: "verpackung",
      icon: Box,
      secondaryIcon: ShieldCheck,
      title: "Verpackungsservice",
      subtitle: "Sicher & Professionell",
      description: "Professionelle Verpackung für alle Arten von Gütern – von empfindlicher Elektronik bis hin zu Kunst und Antiquitäten.",
      features: [
        "Individuelle Verpackungslösungen",
        "Spezialverpackung für fragile Güter",
        "Kunst- und Antiquitätenverpackung",
        "Nachhaltige Verpackungsmaterialien",
        "Umzugskartons und Füllmaterial",
        "Maßanfertigung für Sondergrößen"
      ],
      image: "https://customer-assets.emergentagent.com/job_3f8853ef-18d6-476f-b97c-eda985cb2340/artifacts/bhpk4a4p_Holzkiste_Kunst.jpg"
    }
  ];

  const partners = [
    "UPS", "DHL Express", "DPD", "GLS", "FedEx", "TNT"
  ];

  return (
    <div data-testid="services-page">
      {/* Page Header */}
      <section className="page-header py-20 md:py-28 text-white">
        <div className="container mx-auto px-4 relative z-10">
          <span className="inline-block bg-orange-600 text-white text-sm uppercase tracking-widest font-bold px-4 py-2 mb-6">
            Unser Angebot
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Unsere Leistungen
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl">
            Versand, Express, Fulfillment und Verpackung – alles aus einer Hand für Ihr Unternehmen.
          </p>
        </div>
      </section>

      {/* Partners Bar */}
      <section className="py-8 bg-slate-100 border-b border-slate-200">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <span className="text-slate-500 font-medium">Unsere Partner:</span>
            {partners.map((partner) => (
              <span key={partner} className="text-slate-700 font-bold text-lg">
                {partner}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          {services.map((service, index) => {
            const Icon = service.icon;
            const SecondaryIcon = service.secondaryIcon;
            const isEven = index % 2 === 0;
            
            return (
              <div 
                key={service.id}
                id={service.id}
                className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24 last:mb-0 ${
                  isEven ? "" : "lg:flex-row-reverse"
                }`}
                data-testid={`service-detail-${service.id}`}
              >
                <div className={isEven ? "lg:order-1" : "lg:order-2"}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 bg-orange-600 flex items-center justify-center">
                      <Icon size={28} className="text-white" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-orange-600 uppercase tracking-widest font-bold text-sm">
                        {service.subtitle}
                      </span>
                    </div>
                  </div>
                  
                  <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
                    {service.title}
                  </h2>
                  
                  <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div className="grid sm:grid-cols-2 gap-4 mb-8">
                    {service.features.map((feature, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <CheckCircle 
                          className="text-orange-600 flex-shrink-0 mt-0.5" 
                          size={20} 
                          aria-hidden="true" 
                        />
                        <span className="text-slate-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Link to="/angebot">
                    <Button 
                      className="bg-orange-600 hover:bg-orange-700 text-white uppercase tracking-wider font-bold px-8"
                      data-testid={`service-cta-${service.id}`}
                    >
                      Angebot anfordern
                      <ArrowRight className="ml-2" size={16} aria-hidden="true" />
                    </Button>
                  </Link>
                </div>
                
                <div className={isEven ? "lg:order-2" : "lg:order-1"}>
                  <div className="relative">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full shadow-xl"
                    />
                    <div className={`absolute -bottom-4 ${isEven ? '-right-4' : '-left-4'} bg-slate-950 p-6 hidden md:flex items-center gap-4`}>
                      <SecondaryIcon className="text-orange-500" size={32} aria-hidden="true" />
                      <span className="text-white font-bold uppercase tracking-wider text-sm">
                        {service.subtitle}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-orange-600 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Bereit für besseren Service?
          </h2>
          <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
            Kontaktieren Sie uns für ein individuelles Angebot – wir finden die perfekte Lösung für Ihre Versand- und Logistikanforderungen.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/angebot">
              <Button 
                className="bg-white text-orange-600 hover:bg-slate-100 uppercase tracking-wider font-bold px-10 py-6"
                data-testid="services-cta-quote"
              >
                Angebot anfordern
              </Button>
            </Link>
            <Link to="/kontakt">
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-orange-600 uppercase tracking-wider font-bold px-10 py-6 bg-transparent"
                data-testid="services-cta-contact"
              >
                Kontakt aufnehmen
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
